import { useState, useEffect } from 'react';

// Debug flag to control viewport logging
const DEBUG_VIEWPORT = false;

/**
 * Hook for responsive design that tracks viewport dimensions
 * Used to conditionally render components based on screen size
 * Also checks user agent to detect mobile devices
 * 
 * @returns {Object} Object containing width, height, and screen size category
 */
export function useViewport() {
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  // Check if device is mobile based on user agent
  useEffect(() => {
    const checkMobileDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      
      // Regular expression to check common mobile user agents
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      
      // Set mobile device flag based on regex test or small screen size
      const isMobile = mobileRegex.test(userAgent) || window.innerWidth < 640;
      setIsMobileDevice(isMobile);
      
      // Log for debugging - controlled by debug flag
      if (DEBUG_VIEWPORT) {
        console.log("Device detection:", { 
          userAgent,
          width: window.innerWidth,
          isMobile
        });
      }
    };
    
    checkMobileDevice();
  }, []);
  
  // Update dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
      
      // Update mobile detection on resize
      const isMobile = window.innerWidth < 640;
      setIsMobileDevice(prevState => {
        // Only update if changing from mobile to desktop or vice versa
        if (prevState !== isMobile) {
          if (DEBUG_VIEWPORT) {
            console.log("Mobile state changed:", { previousState: prevState, newState: isMobile });
          }
          return isMobile;
        }
        return prevState;
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Define screen size categories based on Tailwind breakpoints
  // Force mobile layout for mobile devices, small screens, or portrait orientation
  const isMobile = width < 1024 || isMobileDevice || (width < 1024 && height > width) || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent) || (width < 768);
  const isTablet = !isMobile && width >= 1024 && width < 1200;
  const isDesktop = width >= 1200 && !isMobile && !isTablet;
  
  // Debug mobile detection (disabled for production)
  // console.log('🔍 Mobile Detection:', {
  //   width,
  //   height,
  //   isMobileDevice,
  //   isMobile,
  //   orientation: width > height ? 'landscape' : 'portrait',
  //   userAgent: navigator.userAgent
  // });
  
  return { 
    width,
    height,
    isMobile,
    isTablet,
    isDesktop,
    isMobileDevice,
    orientation: width > height ? 'landscape' : 'portrait'
  };
}

export default useViewport; 